import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { usePreviewMode } from "@/components/profile-preview/preview-mode"
import { Card, CardContent } from "@/components/ui/card"
import { creatorKudosPath } from "@/routes"

/**
 * OKS-758 — `/profile` entry point to the kudos surface (UX Spec v2 §4.2).
 *
 * Deliberately DATA-FREE: header row + link, no content preview, no prop.
 *
 * # Why it previews nothing (this is the load-bearing comment)
 *
 * A card that previewed kudo content would need a prop, which means the
 * shared `Profile::ProfileProps` builder — which also serves
 * `CrewProfilePreviewsController#show`, the brand-admin read-only preview.
 * So a "preview card" would silently change the brand-admin preview
 * payload: exactly the small-looking, cross-cutting move the PRD used to
 * REJECT expanding `/activity`, and it would smuggle back the `/profile`
 * kudos section the PRD rejected as Option 3. Data-free keeps `/profile`'s
 * payload byte-identical and holds the ticket at a 3.
 *
 * Accepted cost, stated in the UX Spec: it reads thinner than its
 * neighbours. Deliberate trade, not an oversight — visual parity here costs
 * a cross-cutting prop change and the scope call.
 *
 * # Preview-inert is NOT cosmetic parity with the neighbours
 *
 * `/profile` doubles as the OKS-400 brand-admin read-only preview. An admin
 * previewing a creator's profile has NO creator membership of their own, so
 * `/profile/kudos` (which is `Current.membership`-implicit) would deny them
 * → 404. A live link out of a preview is therefore a BROKEN affordance, not
 * merely an inconsistent one. Hence the same ternary as
 * `badge-preview-block.tsx:41-57` / `recent-activity-card.tsx:60-74`.
 *
 * (For the record, since "brand admin" + "creator profile" in one sentence
 * invites the wrong reading: this is a dead link, NOT a brand-isolation
 * leak. The surface is membership-implicit with no id on the wire, so there
 * is nothing to widen and an admin sees zero kudo data either way.)
 *
 * Locked testids: `kudos-link-card` (wrapper, present in BOTH renders),
 * `kudos-view-all` (the link/span — this is where the live-vs-inert
 * assertion belongs).
 */
export function KudosLinkCard() {
  const { t } = useTranslation()
  const preview = usePreviewMode()

  return (
    <Card data-testid="kudos-link-card" className="border-0 shadow-none">
      <CardContent className="flex flex-col gap-3 px-0 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {t("creator_kudos.preview.heading")}
          </h2>
          {/* OKS-400 — inert in read-only preview (no navigation). */}
          {preview ? (
            <span
              aria-disabled="true"
              className="text-muted-foreground/60 cursor-not-allowed text-sm"
              data-testid="kudos-view-all"
            >
              {t("creator_kudos.preview.view_all")}
            </span>
          ) : (
            <Link
              href={creatorKudosPath()}
              className="text-muted-foreground hover:text-foreground text-sm"
              data-testid="kudos-view-all"
            >
              {t("creator_kudos.preview.view_all")}
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
